import { Button, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import {
  combineValidators,
  maxCharactersValidator,
  requiredValidator,
  uniqueValidator,
} from "@entrilia/shared/utilities/validators";

interface Props {
  onClose: () => void;
  onCreate: (name: string) => void;
  existingInvestorNames: string[];
}

const maxNameLength = 100;

interface FormState {
  name: string;
  validationResult: ValidationResult;
}

const CreateInvestorDialog = ({ onClose, onCreate, existingInvestorNames }: Props) => {
  const [formState, setFormState] = useState<FormState>({
    name: "",
    validationResult: { isValid: false, error: "" },
  });

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const validate = combineValidators(
      requiredValidator,
      maxCharactersValidator(maxNameLength),
      uniqueValidator("An investor with this name already exists", existingInvestorNames)
    );

    setFormState((prev) => ({
      ...prev,
      name: e.target.value,
      validationResult: validate(e.target.value),
    }));
  };

  const handleCreate = () => {
    onClose();
    onCreate(formState.name);
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Add Investor</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <TextField
          fullWidth
          label="Name"
          variant="outlined"
          onChange={handleNameChange}
          value={formState.name}
          helperText={formState.validationResult.error}
          error={!!formState.validationResult.error}
          slotProps={{
            htmlInput: { maxLength: maxNameLength },
            input: {
              endAdornment: (
                <Typography variant="caption" color="textSecondary">
                  {maxNameLength - formState.name.length}
                </Typography>
              ),
            },
          }}
        />
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" disabled={!formState.validationResult.isValid} onClick={handleCreate}>
          Create
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default CreateInvestorDialog;
